import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("مرحباً بك");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex items-end p-12 bg-emerald-950 text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1627728734379-a5f8c099763e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
          }}
        />
        <div className="absolute inset-0 bg-emerald-950/70" />
        <div className="absolute inset-0 islamic-pattern opacity-40" />
        <div className="relative z-10 space-y-6 max-w-md">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-amber-500 flex items-center justify-center shadow-xl shadow-amber-500/30">
              <Moon className="h-7 w-7 text-emerald-950" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-amber-300">Umrah Association</div>
              <div className="font-heading text-xl">نظام إدارة الجمعية</div>
            </div>
          </div>
          <h1 className="font-heading text-5xl font-black leading-tight">
            حين تلتقي الأمانة
            <br />
            <span className="text-amber-400">بالإدارة الرشيدة</span>
          </h1>
          <p className="text-emerald-100/90 text-lg leading-relaxed">
            منظومة متكاملة لإدارة عمليات جمعية العمرة عبر بوابتين مستقلتين — التجارية والخيرية —
            بواجهة عربية أنيقة ودقة تشغيلية عالية.
          </p>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#FDFBF7]">
        <Card className="w-full max-w-md p-8 sm:p-10 border-emerald-900/10 shadow-xl shadow-emerald-950/5">
          <div className="mb-8">
            <div className="lg:hidden mb-6 flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-emerald-900 flex items-center justify-center">
                <Moon className="h-6 w-6 text-amber-400" />
              </div>
              <div className="font-heading text-lg text-emerald-950">جمعية العمرة</div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-emerald-950">تسجيل الدخول</h2>
            <p className="text-slate-500 mt-2">أدخل بياناتك للوصول إلى لوحة التحكم</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-bold text-emerald-950">البريد الإلكتروني</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold text-emerald-950">كلمة المرور</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-12"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-12 bg-emerald-900 hover:bg-emerald-800 active:scale-[0.98] transition-all text-white font-bold text-base rounded-lg"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "دخول"}
            </Button>
          </form>

          <p className="text-xs text-slate-400 mt-8 text-center">
            © {new Date().getFullYear()} جمعية العمرة — نظام إدارة داخلي
          </p>
        </Card>
      </div>
    </div>
  );
}
